// ─── assistant audio out ─────────────────────────────────────────────────────
// Binary protocol frames → speakers, plus the tap the face's MouthAnalyser reads.
// The scheduling arithmetic lives in `PlaybackQueue`; this file is only the Web
// Audio it drives, so the hard part stays testable.
//
// Everything plays through one gain node. A local sound can pause and preserve the
// unheard audio; only a provider-confirmed interruption destroys it.

import type { AudioFormat } from '@shared/protocol.ts'
import { PlaybackQueue } from './queue.ts'
import { MicEncoder, pcm16ToFloat } from './pcm.ts'

export interface SpeakerStats {
  framesPlayed: number
  bytesPlayed: number
  underruns: number
  bufferedSec: number
  dropped: number
  paused: boolean
  heldSec: number
  recovered: number
}

interface LiveSource {
  source: AudioBufferSourceNode
  buffer: AudioBuffer
  playbackRate: number
}

interface HeldAudio {
  buffer: AudioBuffer
  offsetSec: number
  playbackRate: number
}

export interface PlaybackRateCompensator {
  node: AudioNode
  setPlaybackRate(rate: number): void
}

export interface SpeakerOptions {
  /** Native buffer sources require PCM at the context rate; browsers resample themselves. */
  resampleToContext?: boolean
  /** Tempo multiplier. A value other than 1 requires a pitch compensator. */
  playbackRate?: number
  pitchCompensator?: PlaybackRateCompensator
}

const INTERRUPTION_FADE_SEC = 0.012

export class Speaker {
  /** Tap point for `MouthDriver` — assistant audio only, never the mic. */
  readonly tap: GainNode
  private readonly queue = new PlaybackQueue()
  private readonly live = new Map<number, LiveSource>()
  /**
   * A finite source leaves an AudioWorklet with no input bus after it ends. Keep a
   * pitch compensator rendering zeroes between utterances so its buffered DSP tail
   * drains into the gap instead of emerging ahead of the next reply.
   */
  private pitchSilence: ConstantSourceNode | null = null
  private pitchCompensator: PlaybackRateCompensator | null
  /** Null means normal playback; an array (even empty) means provisionally paused. */
  private held: HeldAudio[] | null = null
  private heldUtteranceEnded = false
  private userMuted = false
  private framesPlayed = 0
  private bytesPlayed = 0
  private dropped = 0
  private recovered = 0
  private desiredPlaybackRate: number
  private activePlaybackRate: number
  private resamplers: MicEncoder[] = []

  constructor(
    private readonly ctx: AudioContext,
    private format: AudioFormat,
    private readonly options: SpeakerOptions = {},
  ) {
    this.pitchCompensator = options.pitchCompensator ?? null
    this.desiredPlaybackRate = options.pitchCompensator ? options.playbackRate ?? 1 : 1
    this.activePlaybackRate = this.desiredPlaybackRate
    this.tap = ctx.createGain()
    if (this.pitchCompensator) this.connectPitchCompensator(this.pitchCompensator)
    this.tap.connect(ctx.destination)
  }

  /** Add optional pitch correction only after a caller explicitly opts into it. */
  enablePitchCompensator(compensator: PlaybackRateCompensator): void {
    if (this.pitchCompensator) return
    this.pitchCompensator = compensator
    this.connectPitchCompensator(compensator)
  }

  /** Apply to the next utterance boundary; never retime audio already in flight. */
  setPlaybackRate(rate: number): void {
    if (!Number.isFinite(rate) || rate < 0.8 || rate > 1.25) throw new Error('playback rate must be between 0.8 and 1.25')
    this.desiredPlaybackRate = this.pitchCompensator ? rate : 1
  }

  /** The session can re-declare the output format mid-stream. */
  setFormat(format: AudioFormat): void {
    if (format.sampleRate !== this.format.sampleRate || format.channels !== this.format.channels)
      this.resamplers = []
    this.format = format
  }

  get muted(): boolean {
    return this.userMuted
  }

  setMuted(muted: boolean): void {
    this.userMuted = muted
    this.setGain(muted || this.held !== null ? 0 : 1)
  }

  /** Queue one binary frame. Unsupported encodings are reported, not silently dropped. */
  enqueue(bytes: ArrayBuffer): void {
    if (this.format.encoding !== 'pcm16') throw new Error(`cannot play ${this.format.encoding} audio`)
    const samples = pcm16ToFloat(bytes)
    if (!samples.length) return

    const rate = this.options.resampleToContext ? this.ctx.sampleRate : this.format.sampleRate
    let buffer: AudioBuffer
    if (rate === this.format.sampleRate) {
      // Keep browser playback and native same-rate frames on the direct path.
      buffer = this.ctx.createBuffer(this.format.channels, Math.ceil(samples.length / this.format.channels), rate)
      for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
        const out = buffer.getChannelData(ch)
        for (let i = 0; i < out.length; i++) out[i] = samples[i * buffer.numberOfChannels + ch] ?? 0
      }
    } else {
      // Preserve the fractional position across network packets. Relabelling the
      // buffer or changing playbackRate would distort pitch, timing and lipsync.
      const channels = Array.from({ length: this.format.channels }, (_, ch) => {
        const input = new Float32Array(Math.ceil(samples.length / this.format.channels))
        for (let i = 0; i < input.length; i++) input[i] = samples[i * this.format.channels + ch] ?? 0
        const resampler = this.resamplers[ch] ??= new MicEncoder(this.format.sampleRate, rate)
        return resampler.resample(input)
      })
      if (!channels[0].length) return
      buffer = this.ctx.createBuffer(this.format.channels, channels[0].length, rate)
      for (let ch = 0; ch < buffer.numberOfChannels; ch++) buffer.getChannelData(ch).set(channels[ch])
    }

    this.framesPlayed++
    this.bytesPlayed += bytes.byteLength

    if (this.held !== null) {
      this.held.push({ buffer, offsetSec: 0, playbackRate: this.activePlaybackRate })
      return
    }

    this.schedule(buffer, 0)
  }

  private schedule(buffer: AudioBuffer, offsetSec: number, retainedRate?: number): void {
    if (retainedRate === undefined && !this.queue.playing(this.ctx.currentTime)) {
      this.activePlaybackRate = this.desiredPlaybackRate
      this.pitchCompensator?.setPlaybackRate(this.activePlaybackRate)
    }
    const playbackRate = retainedRate ?? this.activePlaybackRate
    const duration = Math.max(0, buffer.duration - offsetSec) / playbackRate
    if (duration === 0) return
    const slot = this.queue.push(this.ctx.currentTime, duration)
    const source = this.ctx.createBufferSource()
    source.buffer = buffer
    source.playbackRate.value = playbackRate
    // 1.0x is the unprocessed path, even after this Speaker previously used the
    // optional pitch processor. Existing scheduled audio keeps its original route.
    source.connect(playbackRate === 1 ? this.tap : this.pitchCompensator?.node ?? this.tap)
    source.onended = () => this.live.delete(slot.id)
    source.start(slot.startAt, offsetSec)
    this.live.set(slot.id, { source, buffer, playbackRate })
  }

  /**
   * The assistant finished talking (`speech.stop`, or a client driver's whole TTS
   * blob). Nothing is dropped — it only stops the following silence being counted as
   * a dropout.
   */
  endUtterance(): void {
    if (this.held !== null) this.heldUtteranceEnded = true
    else this.queue.expectRestart()
  }

  /**
   * Pause on a local mic edge while retaining every unheard sample. Returns false
   * when there was no assistant audio to interrupt.
   */
  pauseForInterruption(): boolean {
    if (this.held !== null) return this.held.length > 0

    const stopAt = this.ctx.currentTime + INTERRUPTION_FADE_SEC
    const paused = this.queue.pause(stopAt)
    if (!paused.length) return false

    const held: HeldAudio[] = []
    for (const item of paused) {
      const live = this.live.get(item.id)
      if (!live) continue
      this.live.delete(item.id)
      live.source.onended = null
      try {
        live.source.stop(stopAt)
      } catch {
        // already ended; there is no unheard audio from this source to preserve
      }
      held.push({
        buffer: live.buffer,
        offsetSec: item.offsetSec * live.playbackRate,
        playbackRate: live.playbackRate,
      })
    }
    if (!held.length) return false

    this.held = held
    this.heldUtteranceEnded = false
    this.setGain(0, INTERRUPTION_FADE_SEC)
    return true
  }

  /** Resume a provisionally paused reply after a local false alarm. */
  resumeAfterFalseInterruption(): boolean {
    if (this.held === null) return false
    const held = this.held
    const utteranceEnded = this.heldUtteranceEnded
    this.held = null
    this.heldUtteranceEnded = false
    for (const item of held) this.schedule(item.buffer, item.offsetSec, item.playbackRate)
    if (utteranceEnded) this.queue.expectRestart()
    this.setGain(this.userMuted ? 0 : 1, INTERRUPTION_FADE_SEC)
    if (held.length) this.recovered++
    return held.length > 0
  }

  /** Barge-in: drop what has been buffered but not yet heard, within a frame or two. */
  cancel(): void {
    this.resamplers = []
    for (const id of this.queue.clear(this.ctx.currentTime)) {
      const live = this.live.get(id)
      if (!live) continue
      this.live.delete(id)
      live.source.onended = null
      try {
        live.source.stop()
      } catch {
        // never started, or already finished — either way it is silent now
      }
      this.dropped++
    }
    if (this.held !== null) {
      this.dropped += this.held.length
      this.held = null
    }
    this.heldUtteranceEnded = false
    this.setGain(this.userMuted ? 0 : 1)
  }

  get speaking(): boolean {
    // Held audio is still an unfinished utterance. Treating a provisional pause as
    // silence would let idle-conversation timers talk over the eventual recovery.
    return this.queue.playing(this.ctx.currentTime) || this.heldSeconds() > 0
  }

  stats(): SpeakerStats {
    return {
      framesPlayed: this.framesPlayed,
      bytesPlayed: this.bytesPlayed,
      underruns: this.queue.underruns,
      bufferedSec: Number((this.queue.buffered(this.ctx.currentTime) + this.heldSeconds()).toFixed(3)),
      dropped: this.dropped,
      paused: this.held !== null,
      heldSec: Number(this.heldSeconds().toFixed(3)),
      recovered: this.recovered,
    }
  }

  dispose(): void {
    this.cancel()
    if (this.pitchSilence) {
      this.pitchSilence.stop()
      this.pitchSilence.disconnect()
    }
    this.pitchCompensator?.node.disconnect()
    this.tap.disconnect()
  }

  private setGain(value: number, fadeSec = 0): void {
    const gain = this.tap.gain
    const now = this.ctx.currentTime
    gain.cancelScheduledValues(now)
    gain.setValueAtTime(gain.value, now)
    if (fadeSec > 0) gain.linearRampToValueAtTime(value, now + fadeSec)
    else gain.setValueAtTime(value, now)
  }

  private heldSeconds(): number {
    return (this.held ?? []).reduce(
      (seconds, item) => seconds + Math.max(0, item.buffer.duration - item.offsetSec) / item.playbackRate,
      0,
    )
  }

  private connectPitchCompensator(compensator: PlaybackRateCompensator): void {
    compensator.setPlaybackRate(this.activePlaybackRate)
    this.pitchSilence = this.ctx.createConstantSource()
    this.pitchSilence.offset.value = 0
    this.pitchSilence.connect(compensator.node)
    this.pitchSilence.start()
    compensator.node.connect(this.tap)
  }
}
